import { useState, useEffect } from "react";
import { CheckCircle2, Circle, AlertTriangle, ArrowRight, Rocket, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { COUNTRY_DEFAULTS, getChecklistStrings } from "@/config/regionDefaults";

export function OnboardingChecklist({ restaurant, productCount, categoryCount, onStartWizard, onActivated, activationStatus }) {
  const navigate = useNavigate();
  const { authAxios, token } = useAuth();
  const lang = COUNTRY_DEFAULTS[restaurant?.country?.toUpperCase()]?.ui_language || "es";
  const cl = getChecklistStrings(lang);
  const [activationData, setActivationData] = useState(null);
  const [activating, setActivating] = useState(false);
  const [loadingChecklist, setLoadingChecklist] = useState(!activationStatus);

  // If parent passes activationStatus, sync it into local state directly.
  // This avoids stale data after wizard closes without a token change.
  useEffect(() => {
    if (activationStatus) {
      setActivationData(activationStatus);
      setLoadingChecklist(false);
    }
  }, [activationStatus]);

  // Fetch activation checklist via authAxios (30s timeout + auto token refresh).
  // Depend on `token` (stable string) not `authAxios` (new object each render).
  // Skip own fetch when parent provides activationStatus.
  useEffect(() => {
    if (activationStatus) return; // parent owns the data
    let cancelled = false;
    const fetchActivation = async () => {
      try {
        const res = await authAxios.get("/activation/checklist");
        if (!cancelled) setActivationData(res.data);
      } catch (err) {
        console.error("Error fetching activation:", err);
      } finally {
        if (!cancelled) setLoadingChecklist(false);
      }
    };
    fetchActivation();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activationStatus]); // token changes → refetch; authAxios interceptor always uses latest token

  // Action map for each checklist item
  const getAction = (itemId) => {
    const actions = {
      basic_config: () => navigate("/settings?tab=contacto"),
      categories: () => navigate("/menu"),
      products: () => navigate("/menu"),
      operating_hours: () => navigate("/settings?tab=horarios"),
      payment_config: () => navigate("/settings?tab=pagamentos"),
      menu_active: () => navigate("/settings?tab=general"),
    };
    return actions[itemId] || (() => {});
  };

  // Handle activate restaurant
  const handleActivate = async () => {
    if (!activationData?.ready_to_operate) return;

    setActivating(true);
    try {
      const res = await authAxios.post("/activation/validate-and-activate");
      if (res.data?.success !== false) {
        toast.success(cl.activationSuccess);
        setActivationData(prev => ({ ...prev, onboarding_completed: true }));
        onActivated?.();
      } else {
        toast.error(res.data?.message || "Erro ao ativar restaurante");
      }
    } catch (error) {
      console.error("Error activating:", error);
      toast.error(error.response?.data?.detail || "Erro ao ativar restaurante");
    } finally {
      setActivating(false);
    }
  };

  // Loading state
  if (loadingChecklist) {
    return (
      <Card className="border-gray-200">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
          <span className="text-muted-foreground text-sm">{cl.loading}</span>
        </CardContent>
      </Card>
    );
  }

  // If activation data available, use it
  if (activationData) {
    const { checklist, completion_percentage, ready_to_operate, onboarding_completed, status_message } = activationData;
    
    // All done and activated - show success or hide
    if (ready_to_operate && onboarding_completed) {
      return (
        <Card className="border-green-200 bg-green-50/50" data-testid="activation-complete">
          <CardContent className="py-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">{cl.operatingReady}</span>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className={`${ready_to_operate ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}`} data-testid="onboarding-checklist">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {ready_to_operate ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              )}
              {ready_to_operate ? cl.readyTitle : cl.title}
            </CardTitle>
            <span className={`text-sm font-bold ${ready_to_operate ? 'text-green-700' : 'text-amber-700'}`}>
              {completion_percentage}%
            </span>
          </div>
          <div className={`w-full rounded-full h-2 mt-2 ${ready_to_operate ? 'bg-green-200' : 'bg-amber-200'}`}>
            <div 
              className={`h-2 rounded-full transition-all ${ready_to_operate ? 'bg-green-500' : 'bg-amber-500'}`} 
              style={{ width: `${completion_percentage}%` }} 
            />
          </div>
          <p className={`text-xs mt-1 ${ready_to_operate ? 'text-green-700' : 'text-amber-700'}`}>
            {status_message}
          </p>
          
          {/* Quick action buttons */}
          <div className="flex gap-2 mt-2">
            {!ready_to_operate && onStartWizard && (
              <Button
                variant="outline"
                size="sm"
                className="border-amber-400 text-amber-700 hover:bg-amber-100"
                onClick={onStartWizard}
                data-testid="start-wizard-btn"
              >
                {cl.setupNow}
              </Button>
            )}
            {ready_to_operate && !onboarding_completed && (
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 gap-2"
                onClick={handleActivate}
                disabled={activating}
                data-testid="activate-restaurant-btn"
              >
                {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                {activating ? cl.activating : cl.activate}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {checklist.map(item => (
            <button
              key={item.id}
              onClick={getAction(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition text-left ${
                item.completed 
                  ? "text-gray-400" 
                  : ready_to_operate 
                    ? "text-green-800 hover:bg-green-100" 
                    : "text-gray-800 hover:bg-amber-100"
              }`}
              data-testid={`checklist-${item.id}`}
            >
              {item.completed ? (
                <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 shrink-0" />
              ) : (
                <Circle className={`w-4.5 h-4.5 shrink-0 ${ready_to_operate ? 'text-green-400' : 'text-amber-400'}`} />
              )}
              <div className="flex-1">
                <span className={item.completed ? "line-through" : "font-medium"}>{item.name}</span>
                <span className="block text-xs text-muted-foreground">{item.message}</span>
              </div>
              {!item.completed && <ArrowRight className={`w-3.5 h-3.5 ${ready_to_operate ? 'text-green-400' : 'text-amber-400'}`} />}
            </button>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Fallback to basic checks if API not available
  const checks = [
    { key: "name", label: "Nombre del restaurante", done: !!restaurant?.name, action: () => navigate("/settings?tab=contacto") },
    { key: "address", label: "Direccion del restaurante", done: !!restaurant?.address, action: () => navigate("/settings?tab=contacto") },
    { key: "phone", label: "Telefono de contacto", done: !!restaurant?.phone, action: () => navigate("/settings?tab=contacto") },
    { key: "cif", label: "CIF / NIF fiscal", done: !!restaurant?.cif, action: () => navigate("/settings?tab=contacto") },
    { key: "logo", label: "Logomarca del restaurante", done: !!(restaurant?.logo_url || restaurant?.marketing_settings?.logo_url), action: () => navigate("/settings?tab=personalizacion") },
    { key: "category", label: "Al menos 1 categoria creada", done: (categoryCount || 0) >= 1, action: () => navigate("/menu") },
    { key: "products", label: "Al menos 3 productos publicados", done: (productCount || 0) >= 3, action: () => navigate("/menu") },
    { key: "hours", label: "Horarios de funcionamiento", done: (restaurant?.business_hours || []).some(h => h.enabled && h.slots?.some(s => s[0] && s[1])), action: () => navigate("/settings?tab=horarios") },
  ];

  const completed = checks.filter(c => c.done).length;
  const total = checks.length;
  const allDone = completed === total;
  const pct = Math.round((completed / total) * 100);

  if (allDone) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/50" data-testid="onboarding-checklist">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {cl.title}
          </CardTitle>
          <span className="text-sm font-bold text-amber-700">{completed}/{total}</span>
        </div>
        <div className="w-full bg-amber-200 rounded-full h-2 mt-2">
          <div className="h-2 rounded-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-amber-700 mt-1">{cl.completeSteps}</p>
        {onStartWizard && (
          <Button variant="outline" size="sm" className="mt-2 border-amber-400 text-amber-700 hover:bg-amber-100" onClick={onStartWizard} data-testid="start-wizard-btn">
            {cl.setupNow}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-1.5">
        {checks.map(c => (
          <button
            key={c.key}
            onClick={c.action}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition text-left ${c.done ? "text-gray-400" : "text-gray-800 hover:bg-amber-100"}`}
            data-testid={`checklist-${c.key}`}
          >
            {c.done ? <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 shrink-0" /> : <Circle className="w-4.5 h-4.5 text-amber-400 shrink-0" />}
            <span className={c.done ? "line-through" : "font-medium"}>{c.label}</span>
            {!c.done && <ArrowRight className="w-3.5 h-3.5 ml-auto text-amber-400" />}
          </button>
        ))}
      </CardContent>
    </Card>
  );
}
